/**
 * @module services/whatsappConfig
 * @description Servicio para el ABM admin de la integración WhatsApp + LLM.
 *              Endpoints expuestos por components/whatsapp-config/route.js en la API.
 *
 *              Responses tienen los secrets mascarados (apiKey/webhookSecret no
 *              se devuelven, en su lugar `hasApiKey: true/false`).
 */
import api from "./api";

const whatsappConfigService = {
  /**
   * @description Obtiene la config de WhatsApp de la organización.
   *              Si no existe, devuelve un objeto con shape básico.
   * @returns {Promise<Object>} { credentials, ai, messageTemplates, ... }
   */
  getConfig() {
    return api.get("/whatsapp-config");
  },

  /**
   * @description Guarda la config completa. Los secrets (apiKey/webhookSecret)
   *              se preservan si vienen vacíos en el payload — el frontend
   *              debería mandar string vacío cuando el admin no tocó el campo.
   * @param {Object} data - { credentials, ai, messageTemplates }
   * @returns {Promise<Object>} Config guardada (con secrets mascarados)
   */
  saveConfig(data) {
    return api.put("/whatsapp-config", data);
  },

  /**
   * @description Envía un mensaje de prueba al teléfono indicado para
   *              verificar que las credenciales 2chat funcionan.
   * @param {Object} payload
   * @param {string} payload.toPhone - Destino en E.164
   * @param {string} [payload.text]
   * @returns {Promise<Object>} { success, externalMessageId, sandbox }
   */
  testMessage(payload) {
    return api.post("/whatsapp-config/test-message", payload);
  },

  /**
   * @description Sandbox del LLM parser. Permite al admin ver cómo
   *              interpretaría el LLM una respuesta dada sin esperar
   *              a que llegue una real.
   * @param {Object} payload - { taskType, taskName, expectedFormat, patientMessage }
   * @returns {Promise<Object>} Decisión simulada del LLM
   */
  testLlm(payload) {
    return api.post("/whatsapp-config/llm-test", payload);
  },

  /**
   * @description Listado paginado de LlmDecisions para el panel de auditoría.
   * @param {Object} [params] - { confidenceMax, requiresHumanReview, patient, from, to, page, limit }
   * @returns {Promise<{items, total, page, limit}>}
   */
  listDecisions(params = {}) {
    return api.get("/whatsapp-config/decisions", { params });
  },

  /**
   * @description Detalle completo de una LlmDecision (incluye prompt + raw del LLM).
   * @param {string} id
   * @returns {Promise<Object>}
   */
  getDecision(id) {
    return api.get(`/whatsapp-config/decisions/${id}`);
  },

  /**
   * @description Validación humana de una LlmDecision.
   * @param {string} id
   * @param {Object} payload - { isCorrect, correction?, notes? }
   * @returns {Promise<Object>}
   */
  validateDecision(id, payload) {
    return api.put(`/whatsapp-config/decisions/${id}/validate`, payload);
  },

  /**
   * @description Listado paginado del log unificado de WhatsappInteractions
   *              (todos los mensajes IN+OUT). Es la fuente del panel
   *              "Auditoría WhatsApp".
   * @param {Object} [params] - { patient, taskType, status, direction,
   *                              confidenceMax, from, to, page, limit }
   * @returns {Promise<{items, total, page, limit}>}
   */
  listInteractions(params = {}) {
    return api.get("/whatsapp-config/interactions", { params });
  },

  /**
   * @description Detalle completo de una WhatsappInteraction.
   * @param {string} id
   * @returns {Promise<Object>}
   */
  getInteraction(id) {
    return api.get(`/whatsapp-config/interactions/${id}`);
  },

  /**
   * @description Busca pacientes para el selector del modal de dispatch.
   *              Filtra por nombre/teléfono. Solo pacientes con phone
   *              cargado (los que no tienen no pueden recibir WhatsApp).
   * @param {string} q - query string (mín. 2 chars).
   * @returns {Promise<{items: Array}>}
   */
  searchPatientsForDispatch(q) {
    return api.get("/whatsapp-config/dispatch-task/patients", {
      params: { q },
    });
  },

  /**
   * @description Lista tasks NO completadas de un paciente para un tipo.
   *              Excluye las que ya tienen Notification whatsapp.
   * @param {string} patientId
   * @param {'vital'|'checkin'|'medication'} taskType
   * @returns {Promise<{items: Array}>}
   */
  listPendingTasksForDispatch(patientId, taskType) {
    return api.get("/whatsapp-config/dispatch-task/tasks", {
      params: { patientId, taskType },
    });
  },

  /**
   * @description Dispara UNA task específica al paciente vía WhatsApp.
   *              IMPORTANTE: manda mensaje REAL al teléfono del paciente.
   * @param {Object} payload
   * @param {string} payload.patientId
   * @param {'VitalTask'|'CheckinTask'|'MedicationTask'} payload.taskModel
   * @param {string} payload.taskId
   * @returns {Promise<{ok, notificationId, interactionId, conversationId, outboundText}>}
   */
  dispatchTask(payload) {
    return api.post("/whatsapp-config/dispatch-task", payload);
  },
};

export default whatsappConfigService;
